// Android-local CV PDF export. The final PDF is generated ON DEVICE from the
// rendered CV preview DOM (identical fonts/RTL shaping/layout to what the user
// sees) — no backend call, no coins, no watermark, no attestation. On native
// the file is saved to app cache and handed to the OS share sheet; in a
// browser QA build it falls back to a normal download.
import html2pdf from 'html2pdf.js';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { diag } from './diagLog';

// Native SAF save plugin (android/SavePdfPlugin.java): the PDF is written to
// app cache first (proven-reliable path), then Android's real "Save file"
// dialog (ACTION_CREATE_DOCUMENT) is opened and the cache file is stream-
// copied to the user-chosen location with full byte verification.
// Deterministic outcomes: {saved:true,bytes} | {cancelled:true}; rejects ONLY
// on a genuine write failure (and removes the empty document first).
const SavePdf = registerPlugin('SavePdf');

const EXPORT_STYLE_ID = 'cv-pdf-export-fixes';

// All export-only styling lives in ONE stylesheet scoped to html2pdf's
// offscreen `.html2pdf__container`, so the on-screen CV UI is never touched.
// Critically, the SAME rules apply to BOTH copies html2pdf works with:
//   1. the measurement copy (in-document clone used for page-break math), and
//   2. the render copy (html2canvas clones the whole document — including this
//      <style> tag — into its iframe before painting).
// Previously the Arabic fixes were applied via html2canvas `onclone`, which
// only affected copy 2 → the two layouts diverged and page boundaries were
// computed against stale heights, slicing text/blocks mid-content.
const EXPORT_CSS = `
.html2pdf__container #cv-print-root { overflow: visible !important; }

/* The on-screen "paper" chrome (outer padding, card border, rounded corners,
   shadow) is redundant in the PDF — real page margins frame the content — and
   its border/padding used to spill as an ugly sliver onto an extra page and
   paint cut border lines at every page edge. */
.html2pdf__container .cv-paper {
  /* Bottom buffer: html2canvas's render clone can lay out a few px taller
     than the measured container; without a buffer the crop shaves the last
     text line. Any resulting all-white sliver page is trimmed afterwards. */
  padding: 0 0 32px 0 !important;
  overflow: visible !important;
}
.html2pdf__container .cv-paper-inner {
  border: none !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  padding: 0 !important;
}

/* Arabic ascenders (أ، لـ، همزة) paint above the line box; extra line-height
   headroom prevents html2canvas clipping them on tight headings. letter-spacing
   forces per-glyph drawing in html2canvas which destroys Arabic contextual
   shaping — always neutralized. */
.html2pdf__container .cv-name,
.html2pdf__container .cv-job-title,
.html2pdf__container .cv-section-title,
.html2pdf__container .cv-entry-title,
.html2pdf__container .cv-entry-org,
.html2pdf__container .cv-skill-tag,
.html2pdf__container .cv-cert-name,
.html2pdf__container .cv-ref-name {
  line-height: 1.7 !important;
  overflow: visible !important;
  letter-spacing: normal !important;
}

/* A complete logical block moves to the next page instead of being cut. */
.html2pdf__container .cv-entry,
.html2pdf__container .cv-ref-entry,
.html2pdf__container .break-inside-avoid,
.html2pdf__container .cv-personal-header,
.html2pdf__container .cv-summary-text,
.html2pdf__container .cv-exp-notes,
.html2pdf__container .cv-cert-item,
.html2pdf__container .cv-lang-item,
.html2pdf__container .cv-skills-grid,
.html2pdf__container .cv-contact-row,
.html2pdf__container .cv-ref-contact,
.html2pdf__container li {
  break-inside: avoid !important;
  page-break-inside: avoid !important;
}

/* Keep-with-next: extend the heading's box downward (padding) while pulling
   the following content back up (negative margin). Visually identical, but if
   the extended box straddles a page boundary the heading is pushed to the next
   page together with the start of its section — a section title can never sit
   alone at the bottom of a page. */
.html2pdf__container .cv-section-header {
  break-inside: avoid !important;
  page-break-inside: avoid !important;
  padding-bottom: 64px !important;
  margin-bottom: calc(0.75rem - 64px) !important;
}
`;

function injectExportStyles() {
  removeExportStyles();
  const style = document.createElement('style');
  style.id = EXPORT_STYLE_ID;
  style.textContent = EXPORT_CSS;
  document.head.appendChild(style);
}

function removeExportStyles() {
  const el = document.getElementById(EXPORT_STYLE_ID);
  if (el) el.remove();
}

// If the content ends a few px past a page boundary (spacer rounding, trailing
// margins), the last PDF page is a blank sliver. Detect an all-white final
// slice on the rendered canvas and drop that page.
async function trimBlankTrailingPage(worker) {
  try {
    const canvas = await worker.get('canvas');
    const pageSize = await worker.get('pageSize');
    const pdf = await worker.get('pdf');
    const pxPageHeight = Math.floor(canvas.width * pageSize.inner.ratio);
    const nPages = Math.ceil(canvas.height / pxPageHeight);
    if (nPages < 2 || pdf.getNumberOfPages() !== nPages) return;
    const sliceTop = (nPages - 1) * pxPageHeight;
    const data = canvas.getContext('2d')
      .getImageData(0, sliceTop, canvas.width, canvas.height - sliceTop).data;
    for (let i = 0; i < data.length; i += 16) {
      if (data[i] < 250 || data[i + 1] < 250 || data[i + 2] < 250) return;
    }
    pdf.deletePage(nPages);
  } catch (e) {
    // Detection is best-effort (e.g. tainted canvas) — keep the page.
  }
}

export async function exportCvElementToPdf(element, filename) {
  const opt = {
    margin: [8, 8, 10, 8],
    filename,
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: {
      scale: 2.5, useCORS: true, backgroundColor: '#ffffff', logging: false,
    },
    jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
    pagebreak: {
      mode: ['css', 'legacy'],
      avoid: [
        '.break-inside-avoid', '.cv-entry', '.cv-personal-header',
        '.cv-section-header', '.cv-summary-text', '.cv-exp-notes',
        '.cv-cert-item', '.cv-lang-item', '.cv-skills-grid',
        '.cv-contact-row', '.cv-ref-contact', 'li',
      ],
    },
  };

  injectExportStyles();
  try {
    diag('PDF_GEN_START', { filename });
    const worker = html2pdf().set(opt).from(element);
    await worker.toPdf();
    await trimBlankTrailingPage(worker);
    diag('PDF_RENDERED');

    if (Capacitor.isNativePlatform()) {
      // Phase A: generate + validate + stage in app cache. Failures here
      // throw → the caller shows the specific "PDF could not be created" error.
      const dataUri = await worker.outputPdf('datauristring');
      const base64 = dataUri.split(',')[1];
      if (!base64 || !base64.startsWith('JVBERi')) throw new Error('generated data is not a valid PDF');
      diag('PDF_DATA_READY', { kb: Math.round(base64.length / 1366) });
      const staged = await Filesystem.writeFile({ path: filename, data: base64, directory: Directory.Cache });

      // Phase B: Android's real Save dialog + verified native stream copy.
      try {
        const res = await SavePdf.save({ filename, srcPath: staged.uri });
        diag('SAVE_RESULT', res);
        if (res?.saved) return { saved: true };
        return { cancelled: true };
      } finally {
        Filesystem.deleteFile({ path: filename, directory: Directory.Cache }).catch(() => {});
      }
    }

    await worker.save();
    diag('PDF_BROWSER_SAVE');
    return { shared: true };
  } finally {
    removeExportStyles();
  }
}
